/**
 * Local screenshot of the homeowner assistant composer.
 * Development builds only. It does not call the live assistant,
 * Stripe, or a database. The photo and the reply stay in the browser.
 */
import React, { useEffect, useState } from 'react';
import ClientDashboardV3 from './ClientDashboardV3';
import { setHomeownerToken } from '../utils/storage';

const CLIENT = {
  id: 'preview-home',
  email: 'charlie@example.com',
  first_name: 'Charlie',
  last_name: 'Peterson',
  has_password: true,
  tutorial_completed: true,
  password_hash: 'saved',
  password_reset_required: false,
  address: '18 Palm Court',
  features: [],
};

function jsonResponse(body) {
  const text = JSON.stringify(body);
  return {
    ok: true,
    status: 200,
    headers: { get: () => '' },
    json: () => Promise.resolve(body),
    text: () => Promise.resolve(text),
  };
}

function requestUrl(input) {
  if (typeof input === 'string') return input;
  if (input && typeof input.url === 'string') return input.url;
  return '';
}

async function requestBody(init) {
  const body = init && init.body;
  if (typeof body !== 'string') return null;
  try {
    return JSON.parse(body);
  } catch (error) {
    return null;
  }
}

function sampleThread() {
  return { data: [], hasOlder: false };
}

function installPreviewFetch() {
  if (window.__pacarionAssistantPreviewFetch) return;
  const original = window.fetch.bind(window);
  window.__pacarionAssistantPreviewFetch = true;
  window.fetch = async (input, init) => {
    const url = requestUrl(input);
    if (url.indexOf('/.netlify/functions/') === -1) return original(input, init);
    if (url.indexOf('analyze-appliance') !== -1) {
      return jsonResponse({
        analysis: 'This is a State water heater. Flush it once a year and watch for rust-colored water.',
        label: { brand: 'State', serial: '0847A024271' },
      });
    }
    if (url.indexOf('ask-question') !== -1) {
      return jsonResponse({ answer: 'Check it every 90 days.' });
    }
    const body = await requestBody(init);
    if (body && body.resource === 'ai_conversations' && body.action === 'page') {
      return jsonResponse(sampleThread());
    }
    if (url.indexOf('homeowner-client') !== -1) {
      return jsonResponse({
        agent: { first_name: 'Ada', last_name: 'Owner', email: 'ada@example.com' },
        client: CLIENT,
      });
    }
    if (url.indexOf('homeowner-billing') !== -1) {
      return jsonResponse({ plan: { entitled: true, reason: 'sponsored' } });
    }
    return jsonResponse({ data: [], folders: [], homes: [] });
  };
}

function samplePhotoFile() {
  const canvas = document.createElement('canvas');
  canvas.width = 640;
  canvas.height = 480;
  const ctx = canvas.getContext('2d');
  ctx.fillStyle = '#d6d3d1';
  ctx.fillRect(0, 0, 640, 480);
  ctx.fillStyle = '#1c1917';
  ctx.fillRect(78, 36, 484, 408);
  ctx.fillStyle = '#fafaf9';
  ctx.font = 'bold 48px sans-serif';
  ctx.fillText('STATE', 118, 150);
  ctx.font = '28px sans-serif';
  ctx.fillText('Water heater', 118, 214);
  ctx.fillText('Serial 0847A024271', 118, 268);
  return new Promise((resolve) => {
    canvas.toBlob((blob) => {
      resolve(new File([blob], 'water-heater.jpg', { type: 'image/jpeg' }));
    }, 'image/jpeg', 0.92);
  });
}

function setNativeValue(element, value) {
  const prototype = Object.getPrototypeOf(element);
  const descriptor = Object.getOwnPropertyDescriptor(prototype, 'value');
  if (descriptor && descriptor.set) descriptor.set.call(element, value);
  else element.value = value;
  element.dispatchEvent(new Event('input', { bubbles: true }));
}

function waitFor(predicate) {
  return new Promise((resolve) => {
    const started = Date.now();
    const timer = window.setInterval(() => {
      if (predicate()) {
        window.clearInterval(timer);
        resolve(true);
        return;
      }
      if (Date.now() - started > 15000) {
        window.clearInterval(timer);
        resolve(false);
      }
    }, 50);
  });
}

installPreviewFetch();

export default function HomeownerAssistantPreview() {
  const [ready, setReady] = useState(false);
  const search = typeof window !== 'undefined' ? window.location.search : '';
  const params = new URLSearchParams(search);
  const withPhoto = params.get('photo') === '1';
  const sent = params.get('sent') === '1';

  useEffect(() => {
    let cancelled = false;
    setHomeownerToken('preview-homeowner-token').then(() => {
      if (!cancelled) setReady(true);
    });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!ready || !withPhoto) return undefined;
    let cancelled = false;
    const timer = window.setInterval(() => {
      const input = document.querySelector('[data-assistant-thread] input[type="file"]');
      const field = document.querySelector('[data-assistant-input="true"]');
      if (!input || !field || cancelled || input.dataset.previewAttached === '1') return;
      input.dataset.previewAttached = '1';
      window.clearInterval(timer);
      samplePhotoFile().then(async (file) => {
        if (cancelled || !file) return;
        const transfer = new DataTransfer();
        transfer.items.add(file);
        input.files = transfer.files;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        setNativeValue(field, 'What brand is this?');
        const attached = await waitFor(() => document.body.textContent.indexOf('water-heater.jpg') !== -1);
        if (!attached || cancelled) return;
        document.documentElement.dataset.assistantShot = 'before';
        if (!sent) return;
        const button = Array.from(document.querySelectorAll('button')).find((node) => (
          node.textContent && node.textContent.trim() === 'Send'
        ));
        if (button) button.click();
        const done = await waitFor(() => (
          document.body.textContent.indexOf('State water heater') !== -1
          && !document.querySelector('[aria-label="Remove photo"]')
        ));
        if (done) document.documentElement.dataset.assistantShot = 'after';
      });
    }, 50);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [ready, withPhoto, sent]);

  if (!ready) return null;
  return (
    <ClientDashboardV3
      client={CLIENT}
      userId="preview-user"
      onLogout={() => {}}
      initialTab="assistant"
    />
  );
}
